import React, { useState, useEffect } from 'react';
import QRScanner from '../components/attendance/QRScanner';
import { FaQrcode, FaTimesCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt
} from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';

const ScanQRPage = () => {
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  const markAttendance = async (memberData) => {
    if (!eventId) {
      setError('Please select an event first');
      return;
    }

    try {
      setLoading(true);
      
      // Extract SLIITID from member data
      const SLIITID = memberData.SLIITID || memberData.id || '';
      
      if (!SLIITID) {
        throw new Error('No valid member ID found');
      }
      
      // Mark attendance via the API
      const response = await fetch('/Backend/attendance.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          member_id: SLIITID,
          event_id: eventId,
          status: 'present',
          timestamp: new Date().toISOString()
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark attendance');
      }

      const result = await response.json();
      
      setScanResult({
        success: true,
        SLIITID,
        name: memberData.name || memberData.FullName || 'Member',
        message: 'Attendance marked successfully!'
      });
      
      // Reset the scan result after 5 seconds
      setTimeout(() => setScanResult(null), 5000);
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      setError(error.message || 'Failed to mark attendance');
      setScanResult({
        success: false,
        message: error.message || 'Failed to mark attendance'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScanComplete = async (qrData) => {
    if (!eventId) {
      setError('Please select an event first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('QR Code Data Received:', qrData);
      
      // Handle case where qrData is a string (raw QR code data)
      if (typeof qrData === 'string') {
        qrData = qrData.trim();
        console.log('Processing string QR data:', qrData);
        
        // Try to parse as JSON if it looks like JSON
        try {
          const parsedData = JSON.parse(qrData);
          console.log('Parsed QR data as JSON:', parsedData);
          qrData = parsedData;
        } catch (e) {
          console.log('QR data is not JSON, treating as raw SLIITID');
          // Not JSON, continue with string processing
        }
      }
      
      // If we have an attendance response object, extract the member data
      if (qrData && qrData.success && qrData.attendance_id) {
        console.log('Received attendance response, extracting member data from QR code');
        // If this is an attendance response, we need to get the member data from the original QR code
        // For now, we'll show a success message since the attendance was already recorded
        setScanResult({
          success: true,
          message: qrData.message || 'Attendance already recorded',
          isAttendanceResponse: true
        });
        return;
      }
      
      // If we have member data (either from the scanner or after parsing)
      if (qrData && (qrData.id || qrData.SLIITID)) {
        console.log('Using provided member data:', qrData);
        setScanResult({
          success: true,
          member: qrData,
          eventId: eventId
        });
        return markAttendance(qrData);
      }
      
      // Handle different data formats
      let SLIITID = '';
      if (typeof qrData === 'string') {
        // Handle raw SLIITID string (with or without prefix)
        SLIITID = qrData.replace(/^(SLIITID:?\s*)/i, '').trim();
        console.log('Extracted SLIITID from string:', SLIITID);
      } else if (qrData && typeof qrData === 'object') {
        // Handle object with SLIITID in various possible locations
        SLIITID = qrData.SLIITID || 
                 qrData.id || 
                 (qrData.member && (qrData.member.SLIITID || qrData.member.id)) ||
                 '';
                 
        if (SLIITID) {
          // Remove any SLIITID: prefix if present
          SLIITID = String(SLIITID).replace(/^(SLIITID:?\s*)/i, '').trim();
          console.log('Extracted SLIITID from object:', SLIITID);
        }
      }
      
      if (!SLIITID) {
        console.error('No valid SLIITID found in QR code data:', qrData);
        throw new Error('No valid SLIITID found in QR code data. Please ensure the QR code contains a valid member ID.');
      }
      
      // Look up the member to get their details
      const memberResponse = await fetch(`/Backend/QRMember.php?SLIITID=${encodeURIComponent(SLIITID)}`);
      
      if (!memberResponse.ok) {
        throw new Error('Failed to verify member');
      }
      
      const memberData = await memberResponse.json();
      
      if (!memberData.success || !memberData.member) {
        throw new Error('Member not found');
      }
      
      // Use the member's internal ID for attendance
      const response = await fetch('/Backend/attendance_api.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          member_id: memberData.member.id, // Use internal member ID
          event_id: eventId,
          status: 'present'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark attendance');
      }

      const result = await response.json();
      setScanResult({
        success: true,
        SLIITID,
        name: memberData.member.name,
        message: 'Attendance marked successfully!'
      });
      setError(null);
      
      // Reset the scan result after 5 seconds
      setTimeout(() => setScanResult(null), 5000);
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      setError(error.message || 'Failed to mark attendance');
      setScanResult({
        success: false,
        message: error.message || 'Failed to mark attendance'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use relative path to the events.php endpoint with a timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(`https://dev.sliitleo.org/Backend/events.php?_=${timestamp}`, {
          method: 'GET',
          mode: 'cors',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log('API Response:', data); // Debug log
        
        // Check if data is an array (direct response) or has data property
        const eventsData = Array.isArray(data) ? data : (data.data || []);
        
        if (Array.isArray(eventsData)) {
          const formattedEvents = eventsData.map(event => ({
            id: event.id || event.event_id,
            event_name: event.event_name || 'Unnamed Event',
            event_date: event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : '',
            start_time: event.start_time || ''
          })).filter(event => event.id); // Filter out any invalid events
          
          console.log('Formatted Events:', formattedEvents); // Debug log
          setEvents(formattedEvents);
          
          if (formattedEvents.length === 0) {
            setError('No events available. Please create an event first.');
          }
        } else {
          console.error('Unexpected data format:', data);
          throw new Error('Invalid data format received. Expected an array or object with data array.');
        }
      } catch (err) {
        console.error('Error in fetchEvents:', err);
        setError(`Error loading events: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []); // Removed eventId from dependencies to prevent infinite loops
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    SLIITID: '0',
    role: 'Member',
    image: '/user.png',
  };
  return (
    <>
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 ml-0">
        <div className="content">
          {/* Header */}
          <div className="page-head">
            <div>
              <div className="eyebrow">Attendance Management</div>
              <h1>Scan QR Code</h1>
              <p className="page-desc">Scan a member's QR code to mark attendance</p>
            </div>

            {/* Section quick-nav */}
            <div className="filter-row" style={{ flexWrap: 'wrap' }}>
              <Link to="/members" className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faUsers} /> Members
              </Link>
              <Link to="/events" className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faChartPie} /> Events
              </Link>
              <Link to="/scan-qr-code" className="chip active" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faChartBar} /> Scan QR
              </Link>
              <Link to="/attendance" className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faListAlt} /> Attendance
              </Link>
            </div>
          </div>

          <div className="panel" style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
            <div className="field" style={{ marginBottom: 0, maxWidth: 420 }}>
              <label htmlFor="event-select" className="field-label">
                Select Event
              </label>
              <select
                id="event-select"
                value={eventId || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setEventId(value === '' ? null : Number(value));
                  // Save the selected event to localStorage
                  if (value) {
                    localStorage.setItem('lastEventId', value);
                  }
                }}
                disabled={loading || events.length === 0}
              >
                <option value="">Select an event</option>
                {loading ? (
                  <option value="" disabled>Loading events...</option>
                ) : error ? (
                  <option value="" disabled>Error: {error}</option>
                ) : events.length === 0 ? (
                  <option value="" disabled>No events available</option>
                ) : (
                  events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.event_name} - {new Date(event.event_date).toLocaleDateString()}
                      {event.start_time ? ` (${event.start_time.substring(0, 5)})` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>

            {error && (
              <div className="error-banner">
                <FaTimesCircle aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            {scanResult && (
              <div
                className={scanResult.success ? 'success-banner' : 'error-banner'}
                style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  {scanResult.success ? <FaQrcode aria-hidden="true" /> : <FaTimesCircle aria-hidden="true" />}
                  <span style={{ fontWeight: 600 }}>{scanResult.message}</span>
                </div>
                {scanResult.SLIITID && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 25 }}>
                    <span>Name: {scanResult.name || 'N/A'}</span>
                    <span>SLIIT ID: {scanResult.SLIITID}</span>
                    <span className="num" style={{ fontSize: 11, opacity: 0.8 }}>
                      Attendance recorded at {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div>
              <QRScanner
                onScanComplete={handleScanComplete}
                disabled={!eventId || loading}
              />

              {loading && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <span className="spinner lg" aria-hidden="true" />
                  <p style={{ fontSize: 12.5, color: 'var(--text-2)' }}>Processing...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ScanQRPage;
